/**
 * Question provenance — the copy layer for "why was this question asked".
 *
 * This wave grounds every question in something the customer can recognise: the requirement they
 * wrote, the resume they were sent, the vertical's own standard set, or a follow-up composed live.
 * The API VERIFIES every phrase before it stores one (the quoted phrase must genuinely appear in
 * the claimed source text, or the tag is downgraded to "standard" and the phrase dropped) — so by
 * the time these helpers run, a phrase is a verified quote and must be rendered VERBATIM. No
 * paraphrase, no trimming to "clean it up": we show exactly what was checked, or nothing.
 *
 * Every helper answers `null` for an absent/unknown source rather than guessing, because most
 * stored interviews predate this wave: a pre-wave row has no provenance, and "no chip" is the
 * only honest rendering of that. Pure functions on purpose — the web test runner is node-only
 * (no jsdom), so the copy is tested here and the JSX around it stays trivial.
 */

/** Where a PLANNED question was drawn from. The live follow-up adds "adaptive" on transcripts. */
export type PlanQuestionSource = "requirement" | "resume" | "standard";
export type TranscriptQuestionSource = PlanQuestionSource | "adaptive";

/** Per-interview counts from the report payload. null = a pre-wave interview with NO provenance
 *  at all — the caller renders its existing header, never a row of zeros pretending to be data. */
export interface QuestionProvenanceCounts {
  requirement: number;
  resume: number;
  standard: number;
  adaptive: number;
}

export interface SourceChip {
  label: string;
  /** An existing design-system pill class — no new visual language. */
  cls: string;
}

/** Colour follows the meaning the app already uses: teal = the customer's own material,
 *  green = resume-tailored (matches the "Resume-tailored" plan badge), neutral = standard set. */
const CHIP: Record<PlanQuestionSource, SourceChip> = {
  requirement: { label: "From your requirement", cls: "pill t" },
  resume: { label: "From the resume", cls: "pill v" },
  standard: { label: "Standard for this role", cls: "pill n" },
};

/**
 * The chip for a stored source tag, or null when there is nothing honest to claim: a pre-wave
 * question (source absent/null), an unknown future tag, or "adaptive" — which is deliberately NOT
 * a chip, because a follow-up renders as its own "Follow-up — reason" line (see adaptiveLabel).
 * Accepts a plain string so a payload from a newer API can never crash an older bundle.
 */
export function sourceChip(source: string | null | undefined): SourceChip | null {
  if (source === "requirement" || source === "resume" || source === "standard") return CHIP[source];
  return null;
}

/**
 * The quiet one-line quote under a chip — `from your requirement: “…”` — or null when there is
 * no verified phrase to quote. Only "requirement" and "resume" ever carry one: a "standard"
 * question is not drawn from customer text (and a phrase next to it would be exactly the
 * unverified claim the downgrade path exists to strip), and "adaptive" carries a REASON, not a
 * quote, rendered by adaptiveLabel instead. The phrase is interpolated verbatim.
 */
export function sourceQuote(
  source: string | null | undefined,
  phrase: string | null | undefined,
): string | null {
  if (!phrase || phrase.trim().length === 0) return null;
  if (source === "requirement") return `from your requirement: “${phrase}”`;
  if (source === "resume") return `from the resume: “${phrase}”`;
  return null;
}

/**
 * The transcript line for a live follow-up: "Follow-up — <reason>" when the generator recorded
 * why it probed, else a plain "Follow-up — asked live" (which is factual by construction: an
 * adaptive question only exists because it was composed during the interview).
 */
export function adaptiveLabel(reason: string | null | undefined): string {
  const r = reason?.trim();
  return r && r.length > 0 ? `Follow-up — ${r}` : "Follow-up — asked live";
}

/** Defensive count read: a count is a non-negative integer or it is zero. */
function count(n: unknown): number {
  return typeof n === "number" && Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
}

/**
 * The transcript-card headline the founder asked for:
 *   "9 of 14 written from your requirement · 3 from this candidate's resume · 2 follow-ups asked live"
 *
 * null in → null out (a pre-wave interview keeps today's header untouched), and an all-zero
 * payload also answers null — zeros are the absence of data here, never a fact to print. Only
 * non-zero segments render, and whichever segment comes first carries the "of N" total so the
 * headline always anchors the counts to the whole interview.
 */
export function provenanceHeadline(
  p: Partial<QuestionProvenanceCounts> | null | undefined,
  transcriptLength?: number,
): string | null {
  if (!p) return null;
  const requirement = count(p.requirement);
  const resume = count(p.resume);
  const standard = count(p.standard);
  const adaptive = count(p.adaptive);
  const sourced = requirement + resume + standard + adaptive;
  if (sourced === 0) return null;
  // The "of N" anchor is the transcript the reader is LOOKING AT, not the sourced subset — every
  // post-wave interview has at least one sourceless row (the unscored opener strips provenance),
  // so "6 of 7" above a visibly 8-row transcript read as a discrepancy, not a summary.
  const total = transcriptLength != null && transcriptLength >= sourced ? transcriptLength : sourced;

  const segments: string[] = [];
  // ` of ${total}` rides on the first segment only.
  const of = (): string => (segments.length === 0 ? ` of ${total}` : "");
  if (requirement > 0) segments.push(`${requirement}${of()} written from your requirement`);
  if (resume > 0) segments.push(`${resume}${of()} from this candidate's resume`);
  if (standard > 0) segments.push(`${standard}${of()} standard for this role`);
  if (adaptive > 0)
    segments.push(`${adaptive}${of()} follow-up${adaptive === 1 ? "" : "s"} asked live`);
  return segments.join(" · ");
}
